/**
 * Test call numbers (Billing → Test numbers), kept out of the page so they can be tested.
 *
 * A call whose OTHER party is one of these numbers, valid at the call's time and in scope
 * for its business, is never billed to the client; Teeoff is charged internally instead.
 * The backend matches on the last ten digits, so +91 / 0091 / 91 / 0 / bare are one number.
 */
import { TestAdjustmentPreviewDTO, TestCallLine, TestNumberDTO, TestNumberRequest, TestNumberState } from '../../types/billing';
import { istToServerUtc, serverUtcToIstLocal } from './closeBooksRules';

/** Only these may see the page (backend: PLATFORM_ADMIN, MASTER_ADMIN), lower-cased. */
export const TEST_NUMBER_ROLES = ['platform_admin', 'master_admin'];
export const canManageTestNumbers = (role: string | null | undefined): boolean =>
  TEST_NUMBER_ROLES.includes((role || '').toLowerCase());

/** Digits only, the last ten; null when fewer than ten digits. Same rule as the server. */
export const numberKey = (number: string | null | undefined): string | null => {
  const digits = (number || '').replace(/\D/g, '');
  return digits.length < 10 ? null : digits.slice(-10);
};

/** "••3210" — enough to recognise a teammate's phone without printing it in full. */
export const maskKey = (key: string | null | undefined): string => (key ? `••${key.slice(-4)}` : '-');

export interface TestNumberForm {
  number: string;
  label: string;
  note: string;
  /** IST `datetime-local`. */
  validFrom: string;
  /** IST `datetime-local`; blank = open-ended. */
  validUntil: string;
  scopeMode: 'all' | 'selected';
  organizationIds: string[];
  active: boolean;
}

export const emptyForm = (nowIstLocal: string): TestNumberForm => ({
  number: '',
  label: '',
  note: '',
  validFrom: nowIstLocal,
  validUntil: '',
  scopeMode: 'all',
  organizationIds: [],
  active: true,
});

/** An existing number, in the form's terms (UTC → IST). */
export const formFrom = (n: TestNumberDTO): TestNumberForm => ({
  number: n.number,
  label: n.label,
  note: n.note || '',
  validFrom: serverUtcToIstLocal(n.validFrom),
  validUntil: n.validUntil ? serverUtcToIstLocal(n.validUntil) : '',
  scopeMode: n.allBusinesses ? 'all' : 'selected',
  organizationIds: n.scope.map((s) => s.id),
  active: n.active,
});

/** What is wrong with the form, field by field; empty when it can be sent. */
export const formErrors = (f: TestNumberForm, editing: boolean): Record<string, string> => {
  const e: Record<string, string> = {};
  if (!editing && !numberKey(f.number)) e.number = 'Enter at least 10 digits';
  if (!f.label.trim()) e.label = 'Whose phone is it?';
  if (!istToServerUtc(f.validFrom)) e.validFrom = 'Pick a start date and time';
  if (f.validUntil) {
    if (!istToServerUtc(f.validUntil)) e.validUntil = 'Pick a date and time, or leave blank';
    else if (f.validFrom && f.validUntil <= f.validFrom) e.validUntil = 'Must be after the start';
  }
  if (f.scopeMode === 'selected' && f.organizationIds.length === 0) e.scope = 'Choose at least one business, or All businesses';
  return e;
};

/** The request body: times as server UTC; "All businesses" is an empty scope. */
export const toRequest = (f: TestNumberForm, editing: boolean): TestNumberRequest => {
  const body: TestNumberRequest = {
    label: f.label.trim(),
    note: f.note.trim(),
    validFrom: istToServerUtc(f.validFrom) || undefined,
    organizationIds: f.scopeMode === 'all' ? [] : [...f.organizationIds],
    active: f.active,
  };
  if (!editing) body.number = f.number.trim();
  if (f.validUntil) body.validUntil = istToServerUtc(f.validUntil) || undefined;
  else body.openEnded = true;
  return body;
};

export const stateLabel: Record<TestNumberState, string> = {
  ACTIVE: 'Active',
  SCHEDULED: 'Scheduled',
  ENDED: 'Ended',
  DISABLED: 'Withdrawn',
};

export const stateTone: Record<TestNumberState, string> = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  SCHEDULED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  ENDED: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  DISABLED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

/** "All businesses", or the chosen names. */
export const scopeText = (n: TestNumberDTO): string =>
  n.allBusinesses ? 'All businesses' : n.scope.map((s) => s.name || s.id).join(', ');

/** The adjustment's default start: the billing floor, 1 Oct 2026 00:00 UTC, as IST. */
export const ADJUST_FLOOR_IST = '2026-10-01T05:30';

/** Apply only does something when the preview found charges to reverse or rows to write. */
export const hasWork = (p: TestAdjustmentPreviewDTO | null): boolean =>
  !!p && (p.pendingCharges > 0 || p.internalRowsToWrite > 0);

/** The confirmation sentence the operator agrees to. */
export const confirmText = (p: TestAdjustmentPreviewDTO, money: (n: number) => string): string =>
  `Reverse ${p.pendingCharges} charge(s) worth ${money(p.pendingAmount)} across `
  + `${p.byBusiness.filter((b) => b.pendingCharges > 0).length} business(es)`
  + (p.internalRowsToWrite > 0 ? `, and record ${p.internalRowsToWrite} test call(s) against Teeoff` : '')
  + '. Invoiced charges are left alone.';

/** The drill-down: one business's calls, or all of them when none is picked. */
export const callsOf = (calls: TestCallLine[], businessId: string | null | undefined): TestCallLine[] =>
  businessId === undefined || businessId === '' ? calls : calls.filter((c) => (c.businessId || null) === businessId);
